/**
 * City search for the create wizard's location box (INT-0038).
 *
 * Talks ONLY to the API's Google Places proxy (`/api/v1/places/*`): the key
 * stays server-side and the box works worldwide ("City, ST" for US/CA,
 * "City, Country" elsewhere). Replaced the browser-side Nominatim calls that
 * silently returned nothing outside the US/CA.
 *
 * Degrade contract: a 503 from the proxy (no GOOGLE_MAPS_API_KEY) flips
 * `isSearchUnavailable`; the box then accepts free text, shows no
 * suggestions, hides detection, and never toasts.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import _ from 'lodash';

export interface LocationSuggestion {
  /** React key; same value as placeId. */
  id: string;
  placeId: string;
  display: string;
  full: string;
}

const PLACES_BASE = `${process.env.API_URL}/api/v1/places`;
const DEBOUNCE_MS = 250;
// Google bills autocomplete per session token; one token per focus-to-select
// cycle, regenerated after a pick or 3 minutes idle.
const SESSION_IDLE_MS = 3 * 60 * 1000;

function uuidV4(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch {
    return '';
  }
}

export function useLocationSearch(initialValue: string = '') {
  const [location, setLocation] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isSearchUnavailable, setIsSearchUnavailable] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  // Read via refs inside async callbacks so a slow response never overwrites
  // text the user typed in the meantime, and the degrade flag is current.
  const locationRef = useRef(location);
  locationRef.current = location;
  const unavailableRef = useRef(false);
  const latestQueryRef = useRef('');
  const sessionRef = useRef<string>('');
  const sessionTouchedAt = useRef(0);

  const markUnavailable = () => {
    unavailableRef.current = true;
    setIsSearchUnavailable(true);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const sessionToken = () => {
    const now = Date.now();
    if (!sessionRef.current || now - sessionTouchedAt.current > SESSION_IDLE_MS) sessionRef.current = uuidV4();
    sessionTouchedAt.current = now;
    return sessionRef.current;
  };

  // "Use my location": browser geolocation → proxy reverse geocode. Fills only
  // an EMPTY box; every failure (denied, offline, no city) stays silent.
  const detectLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation || unavailableRef.current) return;
    setIsDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const response = await fetch(`${PLACES_BASE}/reverse?lat=${coords.latitude}&lng=${coords.longitude}`);
          if (response.status === 503) { markUnavailable(); return; }
          if (!response.ok) return;
          const data = await response.json();
          const result = data && data.result;
          if (result && result.display && !locationRef.current.trim()) setLocation(result.display);
        } catch {
          /* silent */
        } finally {
          setIsDetecting(false);
        }
      },
      () => setIsDetecting(false),
      { timeout: 10000, maximumAge: 10 * 60 * 1000 }
    );
  }, []);

  const searchLocations = useCallback(
    _.debounce(async (query: string) => {
      if (unavailableRef.current || query.length < 2) {
        setSuggestions([]);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      latestQueryRef.current = query;
      try {
        const response = await fetch(
          `${PLACES_BASE}/autocomplete?input=${encodeURIComponent(query)}&session=${encodeURIComponent(sessionToken())}`
        );
        if (latestQueryRef.current !== query) return; // stale
        if (response.status === 503) { markUnavailable(); return; }
        if (!response.ok) { setSuggestions([]); return; }
        const data = await response.json();
        const list: LocationSuggestion[] = Array.isArray(data?.result)
          ? data.result.map((s: { placeId: string; display: string; full: string }) => ({ id: s.placeId, placeId: s.placeId, display: s.display, full: s.full }))
          : [];
        setSuggestions(list);
        setShowSuggestions(list.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        if (latestQueryRef.current === query) setIsSearching(false);
      }
    }, DEBOUNCE_MS),
    []
  );

  useEffect(() => () => { searchLocations.cancel(); }, [searchLocations]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocation(value);
    setHighlightedIndex(-1);
    if (value.trim()) {
      searchLocations(value.trim());
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion: LocationSuggestion) => {
    setLocation(suggestion.display);
    setSuggestions([]);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    sessionRef.current = ''; // a pick ends the billing session
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) handleSelectSuggestion(suggestions[highlightedIndex]);
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (suggestionsRef.current?.contains(e.relatedTarget as Node)) return;
    setTimeout(() => setShowSuggestions(false), 150);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  return {
    location,
    setLocation,
    suggestions,
    showSuggestions,
    setShowSuggestions,
    isSearching,
    isDetecting,
    isSearchUnavailable,
    detectLocation,
    highlightedIndex,
    setHighlightedIndex,
    suggestionsRef,
    handleInputChange,
    handleSelectSuggestion,
    handleKeyDown,
    handleBlur,
    handleFocus,
  };
}
